'use client';

import { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Blog7 } from '@/components/ui/blog7';
import { getBlogs, getPage, type Blog, type Page } from '@/lib/cms';
import type { Post } from '@/components/ui/blog7';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=800&h=450';

function stripHtml(html: string): string {
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

function formatDate(s?: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function authorName(b: Blog): string {
  if (!b.author) return 'EnthiosHub';
  return typeof b.author === 'string' ? b.author : (b.author as { name?: string }).name ?? 'EnthiosHub';
}

function mapBlogToPost(blog: Blog): Post {
  const summary = blog.excerpt || (blog.content ? stripHtml(blog.content).slice(0, 200) + (blog.content.length > 200 ? '…' : '') : '');
  return {
    id: String(blog.id),
    title: blog.title,
    summary: summary || 'Read more.',
    label: blog.category || 'Article',
    author: authorName(blog),
    published: formatDate(blog.published_at || blog.created_at),
    url: `/blog/${blog.slug}`,
    image: blog.featured_image || PLACEHOLDER_IMAGE,
  };
}

export default function BlogPage() {
  const [page, setPage] = useState<Page | null>(null);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getPage('blog'), getBlogs()])
      .then(([p, list]) => {
        setPage(p ?? null);
        setBlogs(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const tagline = page?.meta_description ? undefined : 'Latest Updates';
  const heading = page?.title || 'Blog & Resources';
  const description = page?.content
    ? stripHtml(page.content).slice(0, 200) + (stripHtml(page.content).length > 200 ? '…' : '')
    : 'Discover the latest trends, tips, and best practices for running events with EnthiosHub.';
  const posts: Post[] = blogs.map(mapBlogToPost);

  return (
    <MainLayout>
      <Blog7
        tagline={tagline ?? 'Latest Updates'}
        heading={heading}
        description={description}
        buttonText="View all articles"
        buttonUrl="/articles"
        posts={posts}
      />
      {!loading && posts.length === 0 && (
        <section className="py-16 px-4 text-center">
          <p className="text-landing-muted">No blog posts yet. Add posts from the admin panel (Content Management → Blogs).</p>
        </section>
      )}
    </MainLayout>
  );
}
